import {
  AddressDetailsDTO,
  BookingDTOIncoterm,
  CreateBookingDTOCargoLCL,
  CustomsDTO,
  LCLItemDTO,
  SustainabilityDTO,
  WaypointBaseDTOTransportMode,
} from '@/clients/booking'
import { CreateBookingDTO } from '@/clients/booking'
import { RequestManualQuoteDTO } from '@/features/bookings/api/quotes'

import { FormBookingDTOCargoFCL } from '../components/BookingDetailsStep/FCLCargoDetails/types'
import { Contact, LegalEntityFormData } from '../components/BookingDetailsStep/LegalEntityDialog/types'
import { BookingCustomType } from '../hooks/useBookingCustomTypeLabels'

export enum LclCargoType {
  ByDimensions = 'byDimensions',
  ByWeightAndVol = 'byWeightAndVol',
}
export type BookingDetailsStepFormState = Pick<
  CreateBookingDTO,
  | 'transportType'
  | 'incoterm'
  | 'cargoReadyAt'
  | 'requestedEtd'
  | 'requestedEta'
  | 'requestedPickupAt'
  | 'requestedDeliveryAt'
  | 'shipperLegalEntityID'
  | 'consigneeLegalEntityID'
  | 'controllingParty'
  | 'unNumbers'
  | 'hasDangerousGoods'
> & {
  cargoFCL?: FormBookingDTOCargoFCL
  mainCarriageMode: WaypointBaseDTOTransportMode[]
  preCarriageMode?: WaypointBaseDTOTransportMode
  onCarriageMode?: WaypointBaseDTOTransportMode
  isMainCarriageHandledByThirdParty: boolean
  pickupAddressDetails?: AddressDetailsDTO
  deliveryAddressDetails?: AddressDetailsDTO
  originPort: string
  destinationPort: string
  cargoLCL: Omit<CreateBookingDTOCargoLCL, 'items'>
  lclCargoType: LclCargoType
  cargoLCLByDimensionsItems: LCLItemDTO[]
  cargoLCLByWeightAndVolItems: LCLItemDTO[]
  shipperContactID?: string | null
  consigneeContactID?: string | null
  shipper?: Contact
  consignee?: Contact
  consigneeLegalEntityCompanyName?: string
  shipperLegalEntityCompanyName?: string
  onBehalfOfShipperLegalEntityID?: string | null
  onBehalfOfConsigneeLegalEntityID?: string | null
  onBehalfOfShipperContactID?: string | null
  onBehalfOfConsigneeContactID?: string | null
  onBehalfOfShipperLegalEntityCompanyName?: string
  onBehalfOfConsigneeLegalEntityCompanyName?: string
  bookingDocuments: BookingDocument[]
}

export type BookingAdditionalServicesStepFormState = Pick<
  CreateBookingDTO,
  | 'insurance'
  | 'carbonOffsetContribution'
  | 'hasDangerousGoods'
  | 'tags'
  // Legacy VGM switch field, used while the VGM local handling flag is off (TTP-7414)
  | 'isVGMBooking'
  | 'isBCNBooking'
  | 'importCustomsService'
  | 'exportCustomsService'
> & {
  customs?: Omit<CustomsDTO, 'originCustomsType' | 'destinationCustomsType'> & {
    originCustomsType: BookingCustomType
    destinationCustomsType: BookingCustomType
  }
  bcnDetails?: {
    numberOfShippersNotClear?: boolean
    numberOfShippers: number
    incoterm: BookingDTOIncoterm
  }
  additionalCargoDetails?: string
  specialEquipment?: boolean
  sustainabilityService?: SustainabilityDTO[]

  // Wizard-only fields (mapped only when the local handling feature flag is enabled)
  extraLocalHandlingServicesEnabled?: boolean
  mainCarriageDocumentationType?: string
  originLocalHandlingService?: string[]
  destinationLocalHandlingService?: string[]
}

export type BookingTermsAndConditionsFormState = {
  generalTermsAndConditionsAccepted: boolean
  noDangerousGoods: boolean
  containerSealingTermsAccepted: boolean
}
export type BookingPriceAndItineraryFormState = BookingTermsAndConditionsFormState &
  Pick<CreateBookingDTO, 'bookingConfirmationRecipients'>

export type BookingQuoteRequestStepFormState = BookingTermsAndConditionsFormState & {
  quoteRequestNotes: string
}

export type BookingShipmentWithQuotesStepFormState = BookingTermsAndConditionsFormState & {
  selectedQuoteID: string | null
}

type DocumentBase = {
  fileName: string
  role: string
  file: File
}

export type MQDocument = DocumentBase
export type BookingDocument = DocumentBase

export type BookingShipmentWithManualQuoteStepFormState = BookingTermsAndConditionsFormState &
  Partial<Pick<RequestManualQuoteDTO, 'customerNotes'>> & {
    additionalRequirements?: Partial<RequestManualQuoteDTO['additionalRequirements']>
    manualQuoteDocuments: MQDocument[]
  }

export type BookingWizardFormState = BookingDetailsStepFormState &
  BookingAdditionalServicesStepFormState &
  Partial<
    BookingPriceAndItineraryFormState &
      BookingShipmentWithQuotesStepFormState &
      BookingShipmentWithManualQuoteStepFormState &
      BookingQuoteRequestStepFormState
  > &
  LegalEntityFormData &
  // Carried through from "Book again" so the created booking links back to its source.
  Pick<CreateBookingDTO, 'clonedFrom'>
